import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { loginSendOTP, loginVerifyOTP } from "../ServiceAPI/login-register/api";
import { Eye, EyeOff, X } from "lucide-react";
import { toast } from "react-toastify";

const LoginModal = ({
  isOpen,
  onClose,
  onLoginSuccess,
  openSignupModal,
  isDark,
  openPaymentModal,
}) => {
  const navigate = useNavigate();
  const [contact, setContact] = useState(""); // email or phone
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("contact"); // "contact" or "otp"
  const [maskedContact, setMaskedContact] = useState("");
  const [otpSentTo, setOtpSentTo] = useState("");

  const handleReset = () => {
    setContact("");
    setOtp("");
    setError("");
    setOtpError("");
    setStep("contact");
  };

  // Step 1: Send OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (!contact || contact.trim().length === 0) {
        setError("Please enter your email or phone number");
        setLoading(false);
        return;
      }

      const response = await loginSendOTP(contact);

      if (response?.success) {
        setMaskedContact(response.masked_contact);
        setOtpSentTo(response.otp_sent_to);
        setStep("otp");
        toast.success("OTP sent successfully!", {
          position: "top-right",
          autoClose: 2000,
        });
      } else {
        setError(response?.error || "Failed to send OTP");
      }
    } catch (err) {
      setError(err.message || "Failed to send OTP. Please try again.");
      console.error("Send OTP error:", err);
    }
    setLoading(false);
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setOtpError("");
    setLoading(true);

    try {
      if (!otp || otp.trim().length === 0) {
        setOtpError("Please enter the OTP");
        setLoading(false);
        return;
      }

      const response = await loginVerifyOTP(contact, otp);

      if (response?.success && response?.token) {
        // Save token and user info
        localStorage.setItem("token", response.token);
        if (response.user) {
          localStorage.setItem("user", JSON.stringify(response.user));
        }

        // Trigger storage event
        window.dispatchEvent(new Event("storage"));

        // Check if user needs to make payment
        if (response.requiresPayment && response.verifiedApplications) {
          toast.success("Login successful!", {
            position: "top-right",
            autoClose: 2000,
          });

          // Close login modal
          onClose();

          // Show payment modal
          if (openPaymentModal) {
            openPaymentModal(response.user.userId, response.verifiedApplications);
          }

          handleReset();
          setLoading(false);
          return;
        }

        // Normal login - redirect to home
        toast.success("Login successful!", {
          position: "top-right",
          autoClose: 2000,
        });

        onClose();
        if (onLoginSuccess) onLoginSuccess();

        navigate("/");
        handleReset();
      } else {
        setOtpError(response?.error || "OTP verification failed");
      }
    } catch (err) {
      setOtpError(err.message || "OTP verification failed. Please try again.");
      console.error("Verify OTP error:", err);
    }
    setLoading(false);
  };

  const handleBackToContact = () => {
    setStep("contact");
    setOtp("");
    setOtpError("");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div
        className={`${
          isDark ? "bg-gray-900" : "bg-white"
        } premium-card max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col`}
      >
        {/* Modal Header */}
        <div
          className={`flex items-center p-6 border-b ${
            isDark
              ? "bg-gray-800/50 border-gray-700"
              : "bg-blue-50/50 border-blue-100"
          }`}
        >
          <h2
            className={`text-2xl font-bold text-center flex-1 ${
              isDark ? "text-gray-100" : "text-gray-800"
            }`}
          >
            {step === "contact" ? "Sign In to RTO Bharat" : "Enter OTP"}
          </h2>
          <button
            onClick={onClose}
            className={`${
              isDark
                ? "text-gray-300 hover:text-gray-100 hover:bg-gray-700"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            } transition-colors p-2 rounded-full`}
          >
            <X size={24} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto">
          {step === "contact" ? (
            // CONTACT ENTRY STEP
            <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr]">
              {/* Left Side - Login Form */}
              <div className="p-8 lg:p-12">
                <div className="mb-10 text-center lg:text-left">
                  <h3
                    className={`text-3xl font-bold ${
                      isDark ? "text-white" : "text-gray-900"
                    } mb-3`}
                  >
                    Welcome Back
                  </h3>
                  <p
                    className={`${
                      isDark ? "text-gray-400" : "text-gray-500"
                    } text-lg`}
                  >
                    Sign in with OTP to access your account
                  </p>
                </div>

                {error && (
                  <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSendOTP} className="space-y-6">
                  {/* Email/Phone Input */}
                  <div>
                    <label htmlFor="contact" className="premium-label">
                      Email or Phone Number
                    </label>
                    <input
                      id="contact"
                      type="text"
                      placeholder="john@example.com or 9876543210"
                      value={contact}
                      onChange={(e) => setContact(e.target.value)}
                      className="premium-input"
                      required
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold py-4 px-6 rounded-xl hover:shadow-lg hover:shadow-blue-500/30 transform transition-all active:scale-95 disabled:opacity-50 disabled:grayscale"
                  >
                    {loading ? "Sending OTP..." : "Send OTP"}
                  </button>

                  {/* Sign Up Link */}
                  <div className="text-center">
                    <p
                      className={`text-sm ${
                        isDark ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      Don't have an account?{" "}
                      <button
                        type="button"
                        onClick={() => {
                          onClose();
                          if (openSignupModal) openSignupModal();
                        }}
                        className="text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200 underline"
                      >
                        Sign Up
                      </button>
                    </p>
                  </div>
                </form>
              </div>

              {/* Right Side - Information Panel */}
              <div className="bg-gradient-to-br from-green-600 via-emerald-700 to-teal-800 p-12 flex flex-col justify-center text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-green-400/10 rounded-full -ml-32 -mb-32 blur-3xl"></div>

                <div className="relative space-y-10">
                  <div>
                    <h4 className="text-2xl font-bold mb-4">Secure OTP Login</h4>
                    <p className="text-green-100 text-lg leading-relaxed opacity-90">
                      Your account is protected with OTP-based authentication. No
                      passwords needed - just your email or phone!
                    </p>
                  </div>

                  {/* Features */}
                  <div>
                    <h4 className="text-lg font-semibold mb-2">Security Features</h4>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                      <ul className="space-y-1 text-xs text-green-100">
                        <li>✓ One-time password for enhanced security</li>
                        <li>✓ 5-minute expiry on every OTP</li>
                        <li>✓ Email and phone verification</li>
                        <li>✓ JWT token-based sessions</li>
                        <li>✓ Encrypted data transmission</li>
                        <li>✓ No password storage on servers</li>
                      </ul>
                    </div>
                  </div>

                  {/* Support */}
                  <div className="border-t border-white/20 pt-4">
                    <h4 className="text-base font-semibold mb-1">Questions?</h4>
                    <p className="text-green-100 text-xs">
                      Our support team is available 24/7<br />
                      Email: support@rto.gov.in<br />
                      Phone: 1800-XXX-XXXX
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // OTP VERIFICATION STEP
            <div className="p-8 lg:p-12 max-w-md mx-auto">
              <div className="mb-8">
                <h3
                  className={`text-2xl font-bold ${
                    isDark ? "text-white" : "text-gray-900"
                  } mb-2`}
                >
                  Verify OTP
                </h3>
                <p
                  className={`${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  We sent an OTP to <strong>{maskedContact}</strong> via{" "}
                  <strong>{otpSentTo}</strong>. Enter it below to continue.
                </p>
              </div>

              {otpError && (
                <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                  {otpError}
                </div>
              )}

              <form onSubmit={handleVerifyOTP} className="space-y-4">
                {/* OTP Input */}
                <div>
                  <label htmlFor="otp" className="premium-label">
                    OTP (6 digits)
                  </label>
                  <input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    placeholder="000000"
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    maxLength={6}
                    className="premium-input text-center text-2xl tracking-widest font-mono"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold py-4 px-6 rounded-xl hover:shadow-lg hover:shadow-green-500/30 transform transition-all active:scale-95 disabled:opacity-50 disabled:grayscale"
                >
                  {loading ? "Verifying..." : "Verify OTP"}
                </button>

                {/* Back Button */}
                <button
                  type="button"
                  onClick={handleBackToContact}
                  disabled={loading}
                  className={`w-full ${
                    isDark
                      ? "bg-gray-800 text-gray-300 hover:bg-gray-700"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  } font-bold py-3 px-6 rounded-xl transition-all`}
                >
                  Back
                </button>

                {/* Resend OTP Link */}
                <p
                  className={`text-center text-sm ${
                    isDark ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Didn't receive OTP?{" "}
                  <button
                    type="button"
                    onClick={handleBackToContact}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Request again
                  </button>
                </p>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
